<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visibleLocal"
    width="92vw"
    top="4vh"
    :append-to-body="true"
    :modal="false"
    :lock-scroll="false"
    :close-on-click-modal="true"
    custom-class="att-preview-dialog"
    @close="onClose"
  >
    <div v-if="loading" class="muted">{{ $t('common.loading') }}</div>
    <div v-else-if="!detail" class="muted">-</div>
    <div v-else>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
        <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ detail.title }}</div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <span class="tag-pill">{{ caseStatusLabel(detail) }}</span>
          <span class="tag-pill">{{ flowLabel(detail) }}</span>
        </div>
      </div>
      <div class="muted" style="margin-top:6px;">{{ detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '-' }}</div>

      <div class="chat" style="margin-top: 12px;">
        <div v-for="b in displayBlocks" :key="b.key">
          <div v-if="b.type==='separator'" class="chat__separator">
            <div class="chat__separatorLine" />
            <span class="tag-pill tag-pill--warn chat__separatorTag">{{ b.label }}</span>
          </div>
          <div v-else :class="['chat__row', b.alignRight ? 'chat__row--mine' : 'chat__row--other']">
            <div :class="['chat__bubble', b.alignRight ? 'chat__bubble--mine' : 'chat__bubble--other']">
              <div class="chat__meta">
                <span class="chat__name">{{ b.senderName || '-' }}</span>
                <span class="muted" style="margin-left:auto;">{{ b.createdAt ? new Date(b.createdAt).toLocaleString() : '-' }}</span>
              </div>
              <div v-if="b.resultCode" style="margin-top:6px;">
                <span class="tag-pill tag-pill--warn">{{ trDisputeResult(b.resultCode) }}</span>
              </div>
              <div v-if="b.text" class="chat__text">{{ b.text }}</div>
              <div v-if="b.allFileIds && b.allFileIds.length>0" style="margin-top: 10px;">
                <AttachmentGallery :file-ids="b.allFileIds" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style="margin-top: 14px; display:flex; gap:10px; flex-wrap:wrap;">
        <!-- Ticket actions -->
        <el-button v-if="(detail.status==='PENDING' || detail.status==='ESCALATED') && !readOnly" type="primary" class="btn-glass" :loading="actionLoading" @click="takeTicket">
          {{ $t('handler.take') }}
        </el-button>
        <el-button v-if="detail.status==='IN_PROGRESS' && !readOnly" :loading="actionLoading" @click="releaseTicket">
          {{ $t('handler.release') }}
        </el-button>
        <el-button v-if="detail.status==='IN_PROGRESS' && !readOnly" type="primary" class="btn-glass" @click="openReplyReturn=true">
          {{ $t('handler.replyReturn') }}
        </el-button>
        <el-button v-if="detail.status==='IN_PROGRESS' && !readOnly" type="primary" class="btn-glass" @click="openReplyClose=true">
          {{ $t('handler.replyClose') }}
        </el-button>

        <!-- Dispute actions (on CLOSED case) -->
        <el-button
          v-if="canTakeDispute && !disputeReadOnly"
          type="primary"
          class="btn-glass"
          :loading="actionLoading"
          @click="takeDisputeAction"
        >
          {{ $t('handler.take') }}
        </el-button>
        <el-button
          v-if="canReleaseDispute && !disputeReadOnly"
          :loading="actionLoading"
          @click="releaseDisputeAction"
        >
          {{ $t('handler.disputeRelease') }}
        </el-button>
        <el-button
          v-if="canCloseDispute && !disputeReadOnly"
          type="primary"
          class="btn-glass"
          @click="openDisputeClose=true"
        >
          {{ $t('handler.disputeClose') }}
        </el-button>
      </div>
    </div>

    <el-dialog
      :title="$t('handler.replyReturn')"
      :visible.sync="openReplyReturn"
      width="560px"
      top="10vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onReplyReturnClose"
    >
      <el-input v-model="replyReturnContent" type="textarea" :rows="6" />
      <div style="margin-top: 10px;">
        <FileUploader ref="replyReturnUploader" v-model="replyReturnFiles" @uploading-change="replyReturnUploading = $event" />
      </div>
      <span slot="footer">
        <el-button @click="openReplyReturn=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || replyReturnUploading"
          :disabled="actionLoading || replyReturnUploading"
          @click="doReplyReturn"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('handler.replyClose')"
      :visible.sync="openReplyClose"
      width="560px"
      top="10vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onReplyCloseClose"
    >
      <el-input v-model="replyCloseContent" type="textarea" :rows="6" />
      <div style="margin-top: 10px;">
        <FileUploader ref="replyCloseUploader" v-model="replyCloseFiles" @uploading-change="replyCloseUploading = $event" />
      </div>
      <span slot="footer">
        <el-button @click="openReplyClose=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || replyCloseUploading"
          :disabled="actionLoading || replyCloseUploading"
          @click="doReplyClose"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="$t('handler.disputeClose')"
      :visible.sync="openDisputeClose"
      width="560px"
      top="10vh"
      :append-to-body="true"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @close="onDisputeCloseClose"
    >
      <el-form label-position="top">
        <el-form-item :label="$t('field.status')">
          <el-select v-model="disputeReply.result" style="width: 100%;">
            <el-option :label="$t('dispute.result.RESOLVED')" value="RESOLVED" />
            <el-option :label="$t('dispute.result.REJECTED')" value="REJECTED" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('field.message')">
          <el-input v-model="disputeReply.content" type="textarea" :rows="6" />
        </el-form-item>
        <el-form-item :label="$t('field.attachments')">
          <FileUploader ref="disputeReplyUploader" v-model="disputeReplyFiles" @uploading-change="disputeReplyUploading = $event" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="openDisputeClose=false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          class="btn-glass"
          :loading="actionLoading || disputeReplyUploading"
          :disabled="actionLoading || disputeReplyUploading"
          @click="doDisputeClose"
        >{{ $t('common.submit') }}</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { handlerCaseDetail, takeCase, releaseCase, replyReturn, replyClose } from '../api/tickets';
import { takeDispute, releaseDispute, closeDispute } from '../api/disputes';
import FileUploader from './FileUploader.vue';
import AttachmentGallery from './AttachmentGallery.vue';
import { parseMessage } from '../utils/message';
import { caseStatusLabel as formatCaseStatusLabel, flowLabel as formatFlowLabel } from '../utils/caseDisplay';

export default {
  name: 'HandlerCaseDialog',
  components: { FileUploader, AttachmentGallery },
  props: {
    value: { type: Boolean, default: false },
    caseNo: { type: String, default: '' },
    // When opened from read-only modules, keep the same detail UI without workflow actions.
    disputeReadOnly: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    detailLoader: { type: Function, default: null }
  },
  data() {
    return {
      visibleLocal: false,
      loading: false,
      actionLoading: false,
      detail: null,
      openReplyReturn: false,
      replyReturnContent: '',
      replyReturnFiles: [],
      replyReturnUploading: false,
      openReplyClose: false,
      replyCloseContent: '',
      replyCloseFiles: [],
      replyCloseUploading: false,
      openDisputeClose: false,
      disputeReply: { result: 'RESOLVED', content: '' },
      disputeReplyFiles: [],
      disputeReplyUploading: false
    };
  },
  computed: {
    dialogTitle() {
      return this.detail ? `${this.$t('ticket.ticketNo')} ${this.detail.caseNo}` : this.$t('ticket.ticketNo');
    },
    meId() {
      return this.$store.state.user ? this.$store.state.user.id : null;
    },
    canTakeDispute() {
      return !this.readOnly && (
        this.detail &&
        this.detail.dispute &&
        this.detail.dispute.status === 'DISPUTE_CREATED'
      );
    },
    canReleaseDispute() {
      return !this.readOnly && (
        this.detail &&
        this.detail.dispute &&
        this.detail.dispute.status === 'DISPUTE_IN_PROGRESS' &&
        this.detail.dispute.assignedHandlerId &&
        this.meId &&
        String(this.detail.dispute.assignedHandlerId) === String(this.meId)
      );
    },
    canCloseDispute() {
      return this.canReleaseDispute;
    },
    replyReturnFileIds() {
      return (this.replyReturnFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    replyCloseFileIds() {
      return (this.replyCloseFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    disputeFileIds() {
      return (this.disputeReplyFiles || []).map((x) => (x ? x.id : null)).filter((x) => x != null);
    },
    displayMessages() {
      const list = this.detail && this.detail.messages ? this.detail.messages : [];
      return list.map((m) => {
        const parsed = parseMessage(m && m.content ? m.content : '');
        const ids = [];
        const seen = new Set();
        const add = (x) => {
          if (x == null) return;
          const n = Number(x);
          if (!n || seen.has(n)) return;
          seen.add(n);
          ids.push(n);
        };
        (m && m.fileIds ? m.fileIds : []).forEach(add);
        (parsed.fileIds || []).forEach(add);
        return {
          ...m,
          mine: !!(m && m.mine),
          alignRight: this.readOnly ? (m && m.senderRole !== 'USER') : !!(m && m.mine),
          text: parsed.text,
          resultCode: parsed.resultCode,
          allFileIds: ids
        };
      });
    },
    displayBlocks() {
      const ms = this.displayMessages || [];
      const blocks = [];
      let inserted = false;
      const hasDispute = !!(this.detail && this.detail.dispute);
      const disputeLabel = hasDispute
        ? `${this.$t('ticket.disputeLabel')}：${this.$t(`status.dispute.${this.detail.dispute.status}`)}`
        : '';

      ms.forEach((m, idx) => {
        if (hasDispute && !inserted && m && String(m.phase || '') === 'DISPUTE') {
          blocks.push({ type: 'separator', key: `sep_dispute_${this.detail.dispute.id || 'x'}`, label: disputeLabel });
          inserted = true;
        }
        blocks.push({ ...m, type: 'message', key: `m_${m.id != null ? m.id : idx}` });
      });

      // If dispute exists but there is no DISPUTE-phase message yet, still show the separator at bottom.
      if (hasDispute && !inserted) {
        blocks.push({ type: 'separator', key: `sep_dispute_${this.detail.dispute.id || 'x'}`, label: disputeLabel });
      }
      return blocks;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.visibleLocal = !!v;
        if (v) this.load();
      }
    },
    visibleLocal(v) {
      this.$emit('input', v);
    },
    caseNo() {
      if (this.visibleLocal) this.load();
    }
  },
  methods: {
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    flowLabel(row) {
      return formatFlowLabel(this, row);
    },
    trDisputeResult(code) {
      const k = `dispute.result.${code}`;
      try {
        if (this.$i18n && this.$i18n.te && this.$i18n.te(k)) return this.$t(k);
      } catch (e) {}
      return code;
    },
    onClose() {
      this.detail = null;
      this.openReplyReturn = false;
      this.openReplyClose = false;
      this.openDisputeClose = false;
      this.onReplyReturnClose();
      this.onReplyCloseClose();
      this.onDisputeCloseClose();
    },
    onReplyReturnClose() {
      try {
        if (this.$refs.replyReturnUploader && this.$refs.replyReturnUploader.cancelUploads) this.$refs.replyReturnUploader.cancelUploads();
      } catch (e) {}
      this.replyReturnContent = '';
      this.replyReturnFiles = [];
      this.replyReturnUploading = false;
    },
    onReplyCloseClose() {
      try {
        if (this.$refs.replyCloseUploader && this.$refs.replyCloseUploader.cancelUploads) this.$refs.replyCloseUploader.cancelUploads();
      } catch (e) {}
      this.replyCloseContent = '';
      this.replyCloseFiles = [];
      this.replyCloseUploading = false;
    },
    onDisputeCloseClose() {
      try {
        if (this.$refs.disputeReplyUploader && this.$refs.disputeReplyUploader.cancelUploads) this.$refs.disputeReplyUploader.cancelUploads();
      } catch (e) {}
      this.disputeReply = { result: 'RESOLVED', content: '' };
      this.disputeReplyFiles = [];
      this.disputeReplyUploading = false;
    },
    async load() {
      if (!this.caseNo) return;
      this.loading = true;
      try {
        this.detail = this.detailLoader ? await this.detailLoader(this.caseNo) : await handlerCaseDetail(this.caseNo);
        this.$emit('reminder-read', this.caseNo);
      } finally {
        this.loading = false;
      }
    },
    bump() {
      this.$store.commit('bumpVersion', 'handlerCases');
      this.$store.commit('bumpVersion', 'disputes');
      try {
        this.$store.dispatch('refreshHandlerBadges');
      } catch (e) {}
    },
    async takeTicket() {
      if (!this.caseNo) return;
      this.actionLoading = true;
      try {
        await takeCase(this.caseNo);
        this.bump();
        this.visibleLocal = false;
        try {
          this.$router.push({ path: '/handler/in-progress', query: { caseNo: this.caseNo } });
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async releaseTicket() {
      if (!this.caseNo) return;
      this.actionLoading = true;
      try {
        await releaseCase(this.caseNo);
        this.bump();
        this.visibleLocal = false;
        try {
          this.$router.push({ path: '/handler/pool', query: { caseNo: this.caseNo } });
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async doReplyReturn() {
      if (!this.caseNo) return;
      if (!this.replyReturnContent) return this.$message.error(this.$t('ticket.requiredContent'));
      try {
        if (this.$refs.replyReturnUploader && this.$refs.replyReturnUploader.isUploading && this.$refs.replyReturnUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await replyReturn(this.caseNo, { content: this.replyReturnContent, fileIds: this.replyReturnFileIds });
        try {
          if (this.$refs.replyReturnUploader && this.$refs.replyReturnUploader.markCommitted) {
            this.$refs.replyReturnUploader.markCommitted(this.replyReturnFileIds);
          }
        } catch (e) {}
        this.openReplyReturn = false;
        this.onReplyReturnClose();
        this.bump();
        await this.load();
      } finally {
        this.actionLoading = false;
      }
    },
    async doReplyClose() {
      if (!this.caseNo) return;
      if (!this.replyCloseContent) return this.$message.error(this.$t('ticket.requiredContent'));
      try {
        if (this.$refs.replyCloseUploader && this.$refs.replyCloseUploader.isUploading && this.$refs.replyCloseUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await replyClose(this.caseNo, { content: this.replyCloseContent, fileIds: this.replyCloseFileIds });
        try {
          if (this.$refs.replyCloseUploader && this.$refs.replyCloseUploader.markCommitted) {
            this.$refs.replyCloseUploader.markCommitted(this.replyCloseFileIds);
          }
        } catch (e) {}
        this.openReplyClose = false;
        this.onReplyCloseClose();
        this.bump();
        await this.load();
      } finally {
        this.actionLoading = false;
      }
    },
    async takeDisputeAction() {
      if (!this.detail || !this.detail.dispute || !this.detail.dispute.id) return;
      this.actionLoading = true;
      try {
        await takeDispute(this.detail.dispute.id);
        this.bump();
        this.visibleLocal = false;
        try {
          if (this.caseNo) this.$router.push({ path: '/handler/in-progress', query: { caseNo: this.caseNo } });
          else this.$router.push('/handler/in-progress');
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async releaseDisputeAction() {
      if (!this.detail || !this.detail.dispute || !this.detail.dispute.id) return;
      this.actionLoading = true;
      try {
        await releaseDispute(this.detail.dispute.id);
        this.bump();
        this.visibleLocal = false;
        try {
          this.$router.push('/handler/disputes');
        } catch (e) {}
      } finally {
        this.actionLoading = false;
      }
    },
    async doDisputeClose() {
      if (!this.detail || !this.detail.dispute || !this.detail.dispute.id) return;
      if (!this.disputeReply.content) return this.$message.error(this.$t('common.required'));
      try {
        if (this.$refs.disputeReplyUploader && this.$refs.disputeReplyUploader.isUploading && this.$refs.disputeReplyUploader.isUploading()) {
          return this.$message.warning(this.$t('common.uploading'));
        }
      } catch (e) {}
      this.actionLoading = true;
      try {
        await closeDispute(this.detail.dispute.id, { result: this.disputeReply.result, content: this.disputeReply.content, fileIds: this.disputeFileIds });
        try {
          if (this.$refs.disputeReplyUploader && this.$refs.disputeReplyUploader.markCommitted) {
            this.$refs.disputeReplyUploader.markCommitted(this.disputeFileIds);
          }
        } catch (e) {}
        this.openDisputeClose = false;
        this.onDisputeCloseClose();
        this.bump();
        await this.load();
      } finally {
        this.actionLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.chat {
  display: grid;
  gap: 10px;
}
.chat__row {
  display: flex;
}
.chat__row--mine {
  justify-content: flex-end;
}
.chat__row--other {
  justify-content: flex-start;
}
.chat__bubble {
  width: min(720px, 100%);
  border-radius: 16px;
  padding: 12px;
  border: 1px solid rgba(226, 232, 240, 0.9);
}
.chat__bubble--mine {
  background: rgba(37, 99, 235, 0.10);
  border-color: rgba(37, 99, 235, 0.20);
}
.chat__bubble--other {
  background: rgba(255, 255, 255, 0.55);
}
.chat__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}
.chat__name {
  font-weight: 600;
}
.chat__text {
  margin-top: 8px;
  white-space: pre-wrap;
  line-height: 1.6;
}
.chat__separator {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
}
.chat__separatorLine {
  flex: 1;
  height: 1px;
  background: rgba(226, 232, 240, 0.95);
}
.chat__separatorTag {
  white-space: nowrap;
}
</style>
